import { yup } from 'shared/core/yup';

export const MEDICAL_CONDITIONS = [
  'Pain (R52)',
  'ADHD (F90)',
  'Anxiety (F41.9)',
  'Insomnia (G47.00)',
  'Arthritis (M19.90)',
  'Cancer (C00-C50)',
  "Crohn's Disease (K50.90)",
  'Depression (F32.9)',
  'Epilepsy (G40)',
  'Glaucoma (H40.89)',
  'HIV/AIDS (B20)',
  'Loss Of Appetite (R630)',
  'Multiple Sclerosis (G35.9)',
  'Muscle Spasms (R252)',
  'Neuropathy (G60.9)',
  "Parkinson's Disease (G20)",
  'Post-Traumatic Stress Disorder (F43.1)',
  'Seizures (G40.409)',
  'Tremors (R25.1)',
  'Autism (F84.0)',
  'Intractable pain for at least 6 months (R52.1)',
  'Hepatitis C (B19.2)',
  'Amyotrophic lateral Sclerosis (ALS) (G12.21)',
  "Tourette's syndrome (F95.2)",
  'Ulcerative Colitis (K51.90)',
  'Severe Arthritis (M19.90)',
  'Fibromyalgia (M79.7)',
  "Alzheimer's Disease (G30.9)",
  'Severe nausea (R11.0)',
  'Severe and persistent muscle spasms (M62.83)',
  'Peripheral neuropathy (G62.9)',
  'Cachexia or wasting syndrome (R64)',
] as const;

export const GDF_LOCATIONS = [
  'Capitol Wellness: Baton Rouge',
  'Cookies by Capitol Wellness: Baton Rouge',
  'Good Day Farm: Bossier City',
  'Good Day Farm: Jennings',
  'Good Day Farm: Lake Charles',
  'Good Day Farm: Natchitoches',
  'Good Day Farm: Shreveport',
  'Good Day Farm: Sulphur',
  'Green Leaf: Houma',
  'Green Leaf: Morgan City',
  'Nola Cannabis Company: Kenner',
  'Nola Cannabis Company: Metairie',
  'Nola Cannabis Company: New Orleans',
  'Story Cannabis: Monroe',
  'Story Cannabis: Ruston',
  'Story Cannabis: West Monroe',
  'The Apothecary Shoppe: Lafayette',
  'The Apothecary Shoppe: New Iberia',
  'The Medicine Cabinet: Alexandria',
  'The Medicine Cabinet: Leesville',
  'The Medicine Cabinet: Marksville',
  'Willow Pharmacy: Covington/Madisonville',
  'Willow Pharmacy: Hammond',
  'Willow Pharmacy: Slidell',
];

export const REQUIRED_TEXT = 'This field is required';

const requiredIfTrue = (schema: yup.AnySchema): yup.AnySchema => schema.required(REQUIRED_TEXT);
const notRequiredIfFalse = (schema: yup.AnySchema): yup.AnySchema => schema.notRequired().transform(() => undefined);

// Everything the intake form asks besides medicalCondition, so GoodCanna V2
// (which collects the condition its own way) can reuse it without also
// requiring the old dropdown field. Concat'd into goodCannaSchema below.
export const goodCannaHealthHistorySchema = yup.object().shape({
  physicallyDisabled: yup.boolean().default(false),

  gender: yup
    .string()
    .default(undefined)
    .required(REQUIRED_TEXT)
    .oneOf(['Male', 'Female'], 'Gender must be either Male or Female'),
  currentlyPregnant: yup.boolean().default(undefined).when('gender', {
    is: 'Female',
    then: requiredIfTrue,
    otherwise: notRequiredIfFalse,
  }),
  currentlyBreastfeeding: yup.boolean().default(undefined).when('gender', {
    is: 'Female',
    then: requiredIfTrue,
    otherwise: notRequiredIfFalse,
  }),

  mentalConditions: yup.boolean().default(false),
  historyOfSuicide: yup.boolean().default(undefined).when('mentalConditions', {
    is: true,
    then: requiredIfTrue,
    otherwise: notRequiredIfFalse,
  }),
  listMentalHealthConditions: yup
    .string()
    .default('')
    .when(['mentalConditions', 'historyOfSuicide'], {
      is: (mentalConditions, historyOfSuicide) => mentalConditions === true && historyOfSuicide === false,
      then: requiredIfTrue,
      otherwise: notRequiredIfFalse,
    }),
  mentalMedications: yup
    .string()
    .default('')
    .when(['mentalConditions', 'historyOfSuicide'], {
      is: (mentalConditions, historyOfSuicide) => mentalConditions === true && historyOfSuicide === false,
      then: requiredIfTrue,
      otherwise: notRequiredIfFalse,
    }),

  bipolar: yup.boolean().default(false),
  bipolarStable: yup.boolean().default(undefined).when('bipolar', {
    is: true,
    then: requiredIfTrue,
    otherwise: notRequiredIfFalse,
  }),
  bipolarMedications: yup.string().default('').when('bipolar', {
    is: true,
    then: requiredIfTrue,
    otherwise: notRequiredIfFalse,
  }),
  bipolarManager: yup.string().default('').when('bipolar', {
    is: true,
    then: requiredIfTrue,
    otherwise: notRequiredIfFalse,
  }),
  bipolarRisk: yup.boolean().default(undefined).when('bipolar', {
    is: true,
    then: requiredIfTrue,
    otherwise: notRequiredIfFalse,
  }),

  opioidsOrLithium: yup.boolean().default(false),
  opioidsOrLithiumReason: yup.string().default('').when('opioidsOrLithium', {
    is: true,
    then: requiredIfTrue,
    otherwise: notRequiredIfFalse,
  }),
  opioidsOrLithiumDoctor: yup.string().default('').when('opioidsOrLithium', {
    is: true,
    then: requiredIfTrue,
    otherwise: notRequiredIfFalse,
  }),

  hospitalizedOrSurguries: yup.boolean().default(false),
  recentHospitalizationReason: yup.string().default('').when('hospitalizedOrSurguries', {
    is: true,
    then: requiredIfTrue,
    otherwise: notRequiredIfFalse,
  }),

  takingMedications: yup.boolean().default(false),
  listOfMedications: yup
    .string()
    .default('')
    .when('takingMedications', {
      is: true,
      then: requiredIfTrue,
      otherwise: (schema) => schema.notRequired().transform(() => undefined),
    }),

  medAllergies: yup.boolean().default(false),
  listOfAllergies: yup.string().default('').when('medAllergies', {
    is: true,
    then: requiredIfTrue,
    otherwise: notRequiredIfFalse,
  }),

  tobaccoUse: yup.boolean().default(false),
  howOftenTobacco: yup.string().default('').when('tobaccoUse', {
    is: true,
    then: requiredIfTrue,
    otherwise: notRequiredIfFalse,
  }),

  alcoholUse: yup.boolean().default(false),
  howOftenAlcohol: yup.string().default('').when('alcoholUse', {
    is: true,
    then: requiredIfTrue,
    otherwise: notRequiredIfFalse,
  }),

  currentMedicalCannabisUse: yup.boolean().default(false),
});

export const goodCannaSchema = yup
  .object()
  .shape({
    medicalCondition: yup.string().default('').required('Medical condition is required'),
  })
  .concat(goodCannaHealthHistorySchema);
